import { useMemo, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Drawer, Icon, Tabs, useToast } from 'barte-design-system';
import { RadioButtonIcon, CircleIcon } from '@phosphor-icons/react';
import DatePicker from 'react-datepicker';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import 'react-datepicker/dist/react-datepicker.css';
import { usePostHog } from '@posthog/react';

import { Title } from '@shared/components';
import { getLocalStorage } from '@shared/constants/local-storage';
import { TRACK_EVENTS } from '@payfac/constants';
import { useGenerateReport, useGenerateBalanceReport } from '@payfac/services';

import { renderCalendarHeader, renderMonthCalendarHeader } from '@shared/components';
import styles from './reports.module.css';
import { EReportApiType } from './reports.type';
import type { PeriodType, ReportConfig } from './reports.type';
import { REPORT_CONFIGS } from './reports.constants';

export const Reports = () => {
    const { t } = useTranslation();
    const toast = useToast();
    const posthog = usePostHog();
    const idCompany = Number(getLocalStorage('companyId'));

    const [selectedReport, setSelectedReport] = useState<ReportConfig | null>(null);
    const [periodType, setPeriodType] = useState<PeriodType>('today');
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [selectedMonth, setSelectedMonth] = useState<Date | null>(null);

    const userEmail = getLocalStorage('userEmail') ?? '';

    const { mutateAsync: generateReport, isPending: isPendingReport } = useGenerateReport();
    const { mutateAsync: generateBalanceReport, isPending: isPendingBalance } = useGenerateBalanceReport();
    const isPending = isPendingReport || isPendingBalance;

    const todayDate = useMemo(() => new Date(), []);
    const isTaxCommissions = selectedReport?.apiType === EReportApiType.tax_commissions;
    const isBalanceSeller = selectedReport?.apiType === EReportApiType.balance_seller;
    const isToday = periodType === 'today';

    useEffect(() => {
        posthog.capture(TRACK_EVENTS.REPORTS.VIEWED);
    }, []);

    const handleOpenDrawer = (report: ReportConfig) => {
        setSelectedReport(report);
        setSelectedMonth(report.apiType === EReportApiType.tax_commissions ? new Date() : null);
    };

    const handleCloseDrawer = () => {
        setSelectedReport(null);
        setPeriodType('today');
        setStartDate(null);
        setEndDate(null);
        setSelectedMonth(null);
    };

    const resolveDateRange = (): { startDate: string; endDate: string } | null => {
        if (isTaxCommissions) {
            if (!selectedMonth) {
                toast.warning({ title: t('payfac.pageReportsSelectDateRange') });

                posthog.capture(TRACK_EVENTS.REPORTS.GENERATED_ERROR, {
                    reportType: selectedReport?.apiType,
                    error: t('payfac.pageReportsSelectDateRange')
                });

                return null;
            }
            return {
                startDate: format(startOfMonth(selectedMonth), 'yyyy-MM-dd'),
                endDate: format(endOfMonth(selectedMonth), 'yyyy-MM-dd'),
            };
        }

        const today = new Date();
        const resolvedStart = isToday ? today : startDate;
        const resolvedEnd = isToday ? today : endDate;

        if (!resolvedStart || !resolvedEnd) {
            toast.warning({ title: t('payfac.pageReportsSelectDateRange') });

            posthog.capture(TRACK_EVENTS.REPORTS.GENERATED_ERROR, {
                reportType: selectedReport?.apiType,
                error: t('payfac.pageReportsSelectDateRange')
            });

            return null;
        }

        return {
            startDate: format(resolvedStart, 'yyyy-MM-dd'),
            endDate: format(resolvedEnd, 'yyyy-MM-dd'),
        };
    };

    const handleGenerateReport = async () => {
        posthog.capture(TRACK_EVENTS.REPORTS.GENERATED_INTENT, {
            reportType: selectedReport?.apiType
        });

        if (!selectedReport || !idCompany) {
            if (!idCompany) {
                toast.error({ title: t('payfac.pageReportsCompanyNotFound') });

                posthog.capture(TRACK_EVENTS.REPORTS.GENERATED_ERROR, {
                    reportType: selectedReport?.apiType,
                    error: t('payfac.pageReportsCompanyNotFound')
                });
            }

            return;
        }

        try {
            if (isBalanceSeller) {
                await generateBalanceReport({
                    idCompany,
                    transferDate: format(new Date(), 'yyyy-MM-dd'),
                    email: userEmail,
                });
            } else {
                const dateRange = resolveDateRange();
                if (!dateRange) return;

                await generateReport({
                    idCompany,
                    type: selectedReport.apiType,
                    startDate: dateRange.startDate,
                    endDate: dateRange.endDate,
                    email: userEmail,
                });
            }

            toast.success({ title: t('payfac.pageReportsGenerateSuccess') });

            posthog.capture(TRACK_EVENTS.REPORTS.GENERATED_SUCCESS, {
                reportType: selectedReport?.apiType
            });

            handleCloseDrawer();
        } catch (error) {
            posthog.capture(TRACK_EVENTS.REPORTS.GENERATED_ERROR, {
                reportType: selectedReport?.apiType,
                error: String(error?.message)
            });

            toast.error({ title: t('payfac.pageReportsGenerateError') });
        }
    };

    return (
        <div className={styles.container}>
            <Helmet>
                <title>{t('payfac.pageReportsTitle')} - Barte</title>
            </Helmet>

            <Title variant="h5" as="h1">{t('payfac.pageReportsTitle')}</Title>

            <div className={styles.grid}>
                {REPORT_CONFIGS.map((report) => (
                    <Card
                        key={report.id}
                        className={styles.card}
                        actions={
                            <Button variant="outline" onClick={() => handleOpenDrawer(report)} size="sm">
                                {t('payfac.pageReportsGenerateButton')}
                            </Button>
                        }
                    >
                        <Title variant="h6" as="h2">{t(report.titleKey)}</Title>
                        <p className={styles.cardDescription}>{t(report.descriptionKey)}</p>
                    </Card>
                ))}
            </div>

            <Drawer
                title={selectedReport ? t(selectedReport.drawerTitleKey) : ''}
                isOpen={!!selectedReport}
                onClose={handleCloseDrawer}
                footer={{
                    secondaryButton: {
                        children: t('payfac.pageReportsCancelButton'),
                        variant: 'outline',
                        onClick: handleCloseDrawer,
                    },
                    primaryButton: {
                        children: t('payfac.pageReportsGenerateButton'),
                        onClick: handleGenerateReport,
                        loading: isPending,
                    },
                }}
            >
                <div className={styles.drawerContent}>
                    <div className={styles.drawerPeriodHeader}>
                        <p className={styles.drawerPeriodTitle}>{t('payfac.pageReportsSelectPeriod')}</p>
                        {selectedReport && (
                            <p className={styles.drawerPeriodDescription}>{t(selectedReport.drawerDescriptionKey)}</p>
                        )}
                    </div>

                    {!isTaxCommissions && !isBalanceSeller && (
                        <Tabs className={styles.periodSelector}>
                            <Tabs.Tab
                                active={isToday}
                                icon={<Icon icon={isToday ? RadioButtonIcon : CircleIcon} />}
                                onClick={() => setPeriodType('today')}
                            >
                                {t('payfac.pageReportsPeriodToday')}
                            </Tabs.Tab>
                            <Tabs.Tab
                                active={periodType === 'period'}
                                icon={<Icon icon={periodType === 'period' ? RadioButtonIcon : CircleIcon} />}
                                onClick={() => setPeriodType('period')}
                            >
                                {t('payfac.pageReportsPeriodCustom')}
                            </Tabs.Tab>
                        </Tabs>
                    )}

                    <Card>
                        <div className={styles.calendarWrapper}>
                            {isTaxCommissions && (
                                <DatePicker
                                    selected={selectedMonth}
                                    onChange={(date: Date | null) => setSelectedMonth(date)}
                                    showMonthYearPicker
                                    inline
                                    locale={ptBR}
                                    calendarClassName={styles.calendar}
                                    renderCustomHeader={renderMonthCalendarHeader}
                                />
                            )}

                            {isBalanceSeller && (
                                <DatePicker
                                    selected={todayDate}
                                    onChange={() => { }}
                                    inline
                                    locale={ptBR}
                                    calendarClassName={`${styles.calendar} ${styles.calendarReadOnly}`}
                                    renderCustomHeader={renderCalendarHeader}
                                />
                            )}

                            {!isTaxCommissions && !isBalanceSeller && (
                                <>
                                    {periodType === 'period' && (
                                        <div className={styles.dateInputsRow}>
                                            <div className={styles.dateInputBox}>
                                                {startDate ? format(startDate, 'dd/MM/yy') : '--/--/--'}
                                            </div>
                                            <span className={styles.dateInputSeparator}>-</span>
                                            <div className={styles.dateInputBox}>
                                                {endDate ? format(endDate, 'dd/MM/yy') : '--/--/--'}
                                            </div>
                                        </div>
                                    )}

                                    {isToday ? (
                                        <DatePicker
                                            selected={todayDate}
                                            onChange={() => { }}
                                            inline
                                            locale={ptBR}
                                            calendarClassName={`${styles.calendar} ${styles.calendarReadOnly}`}
                                            renderCustomHeader={renderCalendarHeader}
                                        />
                                    ) : (
                                        <DatePicker
                                            selectsRange
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={([start, end]) => {
                                                setStartDate(start);
                                                setEndDate(end);
                                            }}
                                            inline
                                            locale={ptBR}
                                            calendarClassName={styles.calendar}
                                            renderCustomHeader={renderCalendarHeader}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    </Card>

                    <div className={styles.alertWrapper}>
                        <Alert
                            variant="info"
                            title={t('payfac.pageReportsEmailAlertTitle')}
                            showCloseButton={false}
                            description={t('payfac.pageReportsEmailAlertDescription', { email: userEmail })}
                        />
                    </div>
                </div>
            </Drawer>
        </div>
    );
};
